import React, { useEffect, useState } from 'react'
import { Button, Drawer, Form, Input, Switch, Typography, Upload } from 'antd'
import styled from 'styled-components'
import { t } from '@/i18n'
import ComponentTitle from '@/pages/safety-equipment/widgets/component-title'
import { IconFont, Message, SvgIcon } from '@/components'
import { useGetPluginDetail, useUpdatePluginDetail } from '../../../utils'
import { useVerificationModeList } from '@/pages/useCase-knowledge-base/template-center/util'
import { formatDateTime } from '@/utils/date'
import DownloadModal from './download-modal'
import lowImg from '@/assets/imges/leak-low.png'
import centerkImg from '@/assets/imges/leak-center.png'
import highImg from '@/assets/imges/leak-high.png'
import severityImg from '@/assets/imges/leak-severity.png'

const { useForm } = Form
const { Text, Paragraph, Link } = Typography

interface ParamItem {
  key: string
  description: string
  default: string
  type: string
  value: string
  isPermanent: number
  isShow: number
  hasChange?: boolean
  filePath?: string
}

interface PropsType {
  id: string
  visible: boolean
  onCancel: () => void
}

const resourceLevelConfig = {
  ResourceLevelSenior: { color: '#fff', bg: '#F44F4F' },
  ResourceLevelMiddle: { color: '#fff', bg: '#FFA709' },
  ResourceLevelJunior: { color: '#fff', bg: '#6F9FFF' },
}

const riskLevelConfig = {
  risk_level_low: { color: '#6F9FFF', bg: '#E1EBFE' },
  risk_level_middle: { color: '#FFA709', bg: '#FFEDCE' },
  risk_level_high: { color: '#FF4141', bg: '#FFE1E1' },
  risk_level_critical: { color: '#B70000', bg: '#F5D9D9' },
}

const DetailDrawer: React.FC<PropsType> = ({ id, visible, onCancel }) => {
  const [form] = useForm()
  const [params, setParams] = useState<ParamItem[]>([])
  const [downloadModalVisible, setDownloadModalVisible] = useState<{
    visible: boolean
    resourceId: string
    sha256: string
  }>({
    visible: false,
    resourceId: '',
    sha256: '',
  })
  const [caseDetail, setCaseDetail] = useState<any>({})
  // 获取用例详情
  const [getCaseDetail] = useGetPluginDetail()
  // 获取验证模式列表
  const [getVerificationModeList, verificationModeList] = useVerificationModeList()
  // 修改用例详情-自定义参数修改
  const [updateCaseDetail, updateCaseDetailLoading] = useUpdatePluginDetail()

  const onFinish = async () => {
    const finalParams = params.map(p => ({
      key: p.key,
      description: p.description,
      default: p.default,
      type: p.type,
      value: p.value,
      isPermanent: p.isPermanent || 0,
      filePath: p.filePath,
      isShow: p.isShow,
    }))

    try {
      const res = await updateCaseDetail({ param: finalParams, plugin_id: caseDetail.resourceId })
      if (!res || !res.data) return
      Message.success(t('修改用例详情-自定义参数修改成功'))
      handleEcho()
    } catch (error) {}
  }

  const handleCancel = () => {
    setParams(caseDetail.param)
    caseDetail.param.forEach((item, index) => {
      if (item.isShow === 1) {
        form.setFieldsValue({ [`${index}value`]: item.value })
      }
    })
  }

  const handleParamChange = (index: number, newValues: Partial<ParamItem>) => {
    setParams(prevParams => {
      const newParams = [...prevParams]
      newParams[index] = { ...newParams[index], ...newValues }
      return newParams
    })
  }

  const handleEcho = () => {
    getCaseDetail({ resourceId: id })
      .then(res => {
        setCaseDetail(res.data)
        setParams(res.data.param)
        res.data.param.forEach((item, index) => {
          if (item.isShow === 1) {
            form.setFieldsValue({ [`${index}value`]: item.value })
          }
        })
      })
      .catch(() => {})
  }

  useEffect(() => {
    if (visible && id) {
      handleEcho()
    }
  }, [id])

  useEffect(() => {
    if (visible) {
      getVerificationModeList().catch(_ => {})
    }
  }, [visible])

  return (
    <Container
      width={600}
      zIndex={1000}
      title={<ComponentTitle title={t('详情')} />}
      open={visible}
      onClose={onCancel}
      destroyOnClose
    >
      <div className="horizontal">
        <div className="label">{t('用例名称：')}</div>
        <div className="value">{caseDetail.pluginName}</div>
      </div>
      <div className="horizontal">
        <div className="label">{t('验证模式：')}</div>
        <div className="value">
          {
            verificationModeList
              .find(item => item.id === String(caseDetail.verificationModeId))
              ?.name.split('->')[0]
          }
          <SvgIcon name="箭头_向右" width={14} height={14} style={{ verticalAlign: 'middle' }} />
          {
            verificationModeList
              .find(item => item.id === String(caseDetail.verificationModeId))
              ?.name.split('->')[1]
          }
        </div>
      </div>
      <div className="horizontal">
        <div className="label">{t('平台架构：')}</div>
        <div className="value">
          {caseDetail.supportedArchitectures?.map((v, i) => (
            <div key={i}>{v == 1 ? 'X86' : 'ARM'}</div>
          ))}
        </div>
      </div>
      <div className="horizontal">
        <div className="label">{t('操作系统：')}</div>
        <div className="value">
          {caseDetail.supportedPlatforms?.includes('1') && (
            <SvgIcon name="windows" width={32} height={32} />
          )}
          {caseDetail.supportedPlatforms?.includes('3') && (
            <SvgIcon name="linux" width={32} height={32} />
          )}
          {caseDetail.supportedPlatforms?.includes('4') && (
            <SvgIcon name="UOS" width={32} height={32} />
          )}
        </div>
      </div>
      {caseDetail.templateName?.length > 0 && (
        <div className="vertical">
          <div className="label">{t('模板名称：')}</div>
          <div className="value">
            {caseDetail.templateName?.map((v, i) => (
              <Text ellipsis={{ tooltip: true }} key={i}>
                {v || '-'}
              </Text>
            ))}
          </div>
        </div>
      )}

      <div className="vertical">
        <div className="label">{t('用例标签：')}</div>
        <div className="value">
          {caseDetail.resourceTagList?.map(item => (
            <div key={item.tagCategoryName} className="group">
              <div className="group-name">{item.tagCategoryName}:</div>
              <div className="group-tags">
                {item.tagList?.map(tag => (
                  <div key={tag.tagName} className="tag">
                    {tag.tagName}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      {params.length > 0 && (
        <div className="vertical">
          <div className="label">{t('用例参数配置：')}</div>
          <div className="value">
            <Form form={form} onFinish={onFinish}>
              <div className="params-deploy">
                {params?.map(
                  (item, index) =>
                    item.isShow === 1 && (
                      <ParamItemComponent
                        key={index}
                        item={item}
                        index={index}
                        form={form}
                        onParamChange={handleParamChange}
                      />
                    ),
                )}
              </div>
              <div className="form-actions">
                <Button onClick={handleCancel}>{t('取消')}</Button>
                <Button
                  type="primary"
                  onClick={() => form.submit()}
                  loading={updateCaseDetailLoading}
                >
                  {t('确认')}
                </Button>
              </div>
            </Form>
          </div>
        </div>
      )}

      {caseDetail.sample?.length > 0 && (
        <div className="vertical">
          <div className="label">{t('样本文件：')}</div>
          <div className="value">
            {caseDetail.sample?.map((item, index) => (
              <div
                key={item.name}
                className="sample"
                style={
                  index === caseDetail.sample.length - 1
                    ? { borderBottom: 'none', paddingBottom: 0 }
                    : {}
                }
              >
                <div className="sample-header">
                  <div>
                    <IconFont type="icon-047-file" style={{ marginLeft: 0 }} />
                    <span>{item.name}</span>
                  </div>
                  <div
                    className="download-btn"
                    onClick={() =>
                      setDownloadModalVisible({
                        visible: true,
                        resourceId: caseDetail.resourceId,
                        sha256: item.sha256,
                      })
                    }
                  >
                    <IconFont type="icon-xiazai2" />
                    <span>{t('下载')}</span>
                  </div>
                </div>
                <div style={{ fontSize: 12, display: 'flex', marginTop: 10 }}>
                  <div className="title">{t('SHA-256：')}</div>
                  <div
                    style={{
                      width: 'calc(100% - 70px)',
                      wordWrap: 'break-word',
                      wordBreak: 'break-all',
                    }}
                  >
                    {item.sha256}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="horizontal">
        <div className="label">{t('用例分级：')}</div>
        <TypeTag
          bgColor={resourceLevelConfig[caseDetail.resourceLevel]?.bg}
          color={resourceLevelConfig[caseDetail.resourceLevel]?.color}
        >
          {caseDetail.resourceLevel === 'ResourceLevelJunior'
            ? t('初级')
            : caseDetail.resourceLevel === 'ResourceLevelMiddle'
            ? t('中级')
            : t('高级')}
        </TypeTag>
      </div>
      <div className="horizontal">
        <div className="label">{t('危险等级：')}</div>
        <div className="value">
          <img
            src={
              caseDetail.riskLevel === 'RiskLevelLow'
                ? lowImg
                : caseDetail.riskLevel === 'RiskLevelMiddle'
                ? centerkImg
                : caseDetail.riskLevel === 'RiskLevelHigh'
                ? highImg
                : severityImg
            }
            alt=""
            style={{ width: '12px', height: '12px' }}
          />
          <TypeTag
            bgColor={riskLevelConfig[caseDetail.riskLevel]?.bg}
            color={riskLevelConfig[caseDetail.riskLevel]?.color}
          >
            {caseDetail.riskLevel === 'RiskLevelLow'
              ? t('低危')
              : caseDetail.riskLevel === 'RiskLevelMiddle'
              ? t('中危')
              : caseDetail.riskLevel === 'RiskLevelHigh'
              ? t('高危')
              : t('严重')}
          </TypeTag>
        </div>
      </div>
      <div className="horizontal">
        <div className="label">{t('攻击类型：')}</div>
        <div className="value">{caseDetail.attackType}</div>
      </div>
      {caseDetail.tactics?.length > 0 && (
        <div className="vertical">
          <div className="label">ATT&CK：</div>
          <div className="value">
            {caseDetail.tactics?.map(item => (
              <Link key={item.name} href={item?.url} target="_blank" className="link">
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="horizontal">
        <div className="label">{t('更新时间：')}</div>
        <div className="value">{formatDateTime(caseDetail.updateTime, 'standard')}</div>
      </div>
      <div className="vertical">
        <div className="label">{t('描述：')}</div>
        <div className="value" style={{ lineHeight: '20px' }}>
          {caseDetail.describe}
        </div>
      </div>
      {caseDetail.principle && (
        <div className="vertical">
          <div className="label">{t('判断原理：')}</div>
          <div className="value" style={{ lineHeight: '20px' }}>
            {caseDetail.principle}
          </div>
        </div>
      )}
      <div className="vertical">
        <div className="label">{t('解决方案：')}</div>
        <div className="value" style={{ lineHeight: '20px' }}>
          {caseDetail.solution}
        </div>
      </div>
      <DownloadModal
        visible={downloadModalVisible.visible}
        onClose={() => setDownloadModalVisible({ visible: false, resourceId: '', sha256: '' })}
        resourceId={downloadModalVisible.resourceId}
        sha256={downloadModalVisible.sha256}
      />
    </Container>
  )
}

const ParamItemComponent = ({ item, index, form, onParamChange }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [canExpand, setCanExpand] = useState(false)

  const commonInputProps = {
    style: { width: 465 },
    onChange: e => {
      onParamChange(index, {
        value: e.target.value,
        isPermanent: item.isPermanent === 1 ? item.isPermanent : 2,
        hasChange: true,
        type: 'text',
      })
    },
  }

  const renderInput = () => {
    switch (item.type) {
      case 'password':
        return (
          <Form.Item
            name={`${index}CommandValue`}
            rules={[
              { max: 1024, message: t('参数最多为1024字') },
              { required: true, message: t('请输入参数') },
            ]}
            validateFirst
          >
            <Input
              {...commonInputProps}
              type="password"
              onFocus={() => onParamChange(index, { value: '' })}
              onBlur={() => form.setFieldsValue({ [`${index}CommandValue`]: '●●●●●●●●' })}
            />
          </Form.Item>
        )
      case 'text':
        return (
          <Form.Item
            name={`${index}CommandValue`}
            rules={[
              { max: 1024, message: t('参数最多为1024字') },
              { required: true, message: t('请输入参数') },
            ]}
            validateFirst
          >
            <Input {...commonInputProps} type="text" />
          </Form.Item>
        )
      case 'file':
        return <FileInput item={item} index={index} form={form} onParamChange={onParamChange} />
      case 'bool':
        return <BoolInput item={item} index={index} onParamChange={onParamChange} />
      default:
        return (
          <Form.Item
            name={`${index}value`}
            rules={[
              { max: 1024, message: t('参数最多为1024字') },
              { required: true, message: t('请输入参数') },
              {
                pattern: item.type === 'int' ? /^[0-9]*$/ : RegExp(''),
                message: item.type === 'int' ? t('参数仅支持数字') : '',
              },
              {
                pattern: item.type === 'float' ? /^(\d+)(\.\d+)?$/ : RegExp(''),
                message: item.type === 'float' ? t('参数仅支持数字和小数') : '',
              },
            ]}
            validateFirst
          >
            <Input
              style={{ width: 465 }}
              onChange={e =>
                onParamChange(index, {
                  value: e.target.value,
                  isPermanent: item.isPermanent === 1 ? item.isPermanent : 2,
                })
              }
            />
          </Form.Item>
        )
    }
  }

  return (
    <div className="params-flex">
      <Text
        className="params-title"
        ellipsis={{
          tooltip: { getPopupContainer: () => document.body },
        }}
      >
        {item.key}
      </Text>
      <div className="content">
        <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>{renderInput()}</div>
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          <Paragraph
            className="describe"
            ellipsis={!isExpanded ? { rows: 1, onEllipsis: setCanExpand } : false}
          >
            {item.description}
          </Paragraph>
          {canExpand && (
            <span onClick={() => setIsExpanded(!isExpanded)} className="expand-btn">
              {isExpanded ? t('收起') : t('展开')}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

const FileInput = ({ item, index, form, onParamChange }) => (
  <div>
    <Form.Item
      name={`${index}value`}
      validateFirst
      rules={[{ required: true, message: t('请上传文件') }]}
    >
      <Upload
        name="file"
        action={`${
          process.env.NODE_ENV === 'dev' ? '/api' : ''
        }/portal/verify/endpointSecurity/uploadParamFile`}
        maxCount={1}
        onChange={({ file }) => {
          if (file.status === 'done') {
            const { code, data } = file.response
            if (code === 200) {
              onParamChange(index, {
                value: data?.name,
                filePath: data?.path,
                isPermanent: item.isPermanent === 1 ? item.isPermanent : 2,
              })
            }
          } else if (file.status === 'error') {
            Message.error(t('上传失败'))
          }
        }}
      >
        <Button type="primary">{t('上传文件')}</Button>
      </Upload>
    </Form.Item>
    {item.value && (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginTop: 12,
        }}
      >
        <IconFont type="icon-fujian2" />
        <Typography.Text
          style={{ marginRight: 36, maxWidth: 240 }}
          ellipsis={{
            tooltip: { getPopupContainer: () => document.body },
          }}
        >
          {item.value}
        </Typography.Text>
        <IconFont
          type="icon-shanchu-m"
          style={{ cursor: 'pointer' }}
          onClick={() => {
            onParamChange(index, { filePath: '', value: '' })
            form.setFieldValue(`${index}value`, null)
          }}
        />
      </div>
    )}
  </div>
)

const BoolInput = ({ item, index, onParamChange }) => (
  <Form.Item name={`${index}value`} validateFirst valuePropName="checked">
    <Switch
      onChange={(e: boolean) => {
        onParamChange(index, {
          value: e.toString(),
          isPermanent: item.isPermanent === 1 ? item.isPermanent : 2,
        })
        Message.success(e ? t('参数已启用') : t('参数已停用'))
      }}
    />
  </Form.Item>
)

export default DetailDrawer

const Container = styled(Drawer)`
  .ant-drawer-close {
    width: 36px;
    height: 36px;
    background: #d8d6f4;
    box-shadow: -1px 4px 10px 0px #c7c7c7;
    border-radius: 6px;
    border: none;
    margin-right: 0px;
    color: #4238c9;
    font-size: 16px;
    font-weight: 500;
    position: absolute;
    right: 0px;
    top: 0px;
    transition: transform 0.3s ease;

    &:hover {
      transform: translate(-10px, 8px);
      box-shadow: 0 0 0 0;
    }

    .ant-drawer-close-x {
      width: 100%;
      height: 100%;
      line-height: 36px;
      text-align: center;
    }
  }

  .ant-drawer-header {
    height: 56px;
    background: #f2f0ff;
    border-bottom: none;
    margin: 0;
  }

  .ant-drawer-body {
    padding: 24px 24px 0 24px;

    .horizontal {
      display: flex;
      align-items: center;
      margin-bottom: 24px;

      .label {
        width: 70px;
        height: 14px;
        color: #86909c;
      }

      .value {
        display: flex;
        align-items: center;
        gap: 8px;
      }
    }

    .vertical {
      display: flex;
      flex-direction: column;
      margin-bottom: 24px;

      .label {
        height: 14px;
        color: #86909c;
        margin-bottom: 8px;
      }

      .value {
        width: 552px;
        background: #f8f9fb;
        border-radius: 6px;
        padding: 10px;
        gap: 10px;
        display: flex;
        flex-direction: column;

        .group {
          display: flex;
          align-items: center;
          gap: 8px;

          .group-name {
            font-size: 14px;
            color: #333333;
          }

          .group-tags {
            display: flex;
            align-items: center;
            gap: 4px;

            .tag {
              font-size: 12px;
              color: #333333;
              height: 20px;
              background: #ededed;
              border-radius: 4px;
              padding: 4px;
            }
          }
        }

        .params-deploy {
          display: flex;
          flex-direction: column;

          .params-flex {
            display: flex;
            justify-content: space-between;
            gap: 8px;

            .params-title {
              line-height: 32px;
            }

            .describe {
              width: 440px;
              font-size: 12px;
              color: #999999;
            }

            .expand-btn {
              color: #4238c9;
              cursor: pointer;
              font-size: 12px;

              &:hover {
                text-decoration: underline;
              }
            }

            .ant-form-item {
              margin-bottom: 0;
            }
          }
        }

        .sample {
          display: flex;
          flex-direction: column;
          border-bottom: 1px dashed #d5d9dd;
          padding-bottom: 10px;

          .sample-header {
            display: flex;
            justify-content: space-between;
            align-items: center;

            span {
              margin-left: 4px;
            }

            .download-btn {
              color: #4238c9;
              font-size: 12px;
              margin-right: 2px;

              &:hover {
                cursor: pointer;
                text-decoration: underline;
              }
            }
          }
        }

        .link {
          color: #333;

          &:hover {
            color: #4238c9;
            text-decoration: underline;
          }
        }

        .form-actions {
          text-align: right;

          .ant-btn {
            margin-left: 8px;
          }
        }
      }
    }
  }
`

const TypeTag = styled.span<{ bgColor: string; color: string }>`
  display: inline-block;
  height: 18px;
  padding: 0 4px;
  background: ${props => props.bgColor};
  border-radius: 4px;
  line-height: 18px;
  color: ${props => props.color};
  font-size: 12px;
  margin-right: 4px;
`
